import * as React from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { getElementByIdFromTemplate } from "@app/access-reports/shared/utility";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessReportsAccessAnalysisElementEnum } from "@app/access-reports/shared/accessReportsAccessAnalysisElementEnum";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";

interface AccessReportsAdminAddProductDetailsProps {
  disableUserActions: boolean;
  groups: string[];
  hiddenAccessStandards: number[];
  hiddenCalculationMethods: number[];
  hiddenDistanceMethods: number[];
  invalidElements: Map<string, string>;
  onAccessAnalysisDefaultChange: (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum) => void;
  onAccessAnalysisHideChange: (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum) => void;
  onAddGroupClick: (group: string) => void;
  onAllowEditToggle: () => void;
  onProductInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onProductTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onRemoveGroupClick: (group: string) => void;
  onReportTitleInputChange: (reportTitleElement: AccessReportsCoverPageElementEnum, event: React.ChangeEvent<HTMLInputElement>) => void;
  onSkipAccessAnalysisToggle: () => void;
  product: AccessReportsWizardProduct;
  productType: AccessReportsWizardOption;
  productTypes: AccessReportsWizardOption[];
  reportTitleOne: string;
  reportTitleTwo: string;
  reportTitleThree: string;
  template: AccessReportsWizardTemplate;
}

interface ProductDetailsTableData {
  [wafflesKeyColumnName]: string;
  default: JSX.Element;
  hide: JSX.Element;
  title: string;
}

const AccessReportsAdminAddProductDetails: React.FC<AccessReportsAdminAddProductDetailsProps> = (props: AccessReportsAdminAddProductDetailsProps) => {
  const [group, setGroup] = React.useState("");

  let productTypeValue = "";
  if (!!props.productType) {
    productTypeValue = props.productType.id.toString();
  }

  const buildAccessAnalysisTable = (accessAnalysisElement: AccessReportsAccessAnalysisElementEnum): React.ReactNode => {
    const element: AccessReportsWizardElement = getElementByIdFromTemplate(props.template, AccessReportsWizardStepEnum.AccessAnalysis, accessAnalysisElement);
    const defaultOption: number = element?.options.find(x => x.defaultProducts.includes(props.product.id))?.id;
    const title = !!element ? element.title : "";
    const options: AccessReportsWizardOption[] = !!element ? element.options : [];

    let hiddenOptions: number[] = [];

    switch (accessAnalysisElement) {
      case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
        hiddenOptions = props.hiddenCalculationMethods;
        break;
      case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
        hiddenOptions = props.hiddenDistanceMethods;
        break;
      case AccessReportsAccessAnalysisElementEnum.AccessStandard:
        hiddenOptions = props.hiddenAccessStandards;
        break;
      default:
        break;
    }

    const data: ProductDetailsTableData[] = options.map((option) => {
      /* oxlint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: option.displayName ? option.displayName : option.name,
        title: option.displayName ? option.displayName : option.name,
        default: (
          <QESRadioButton
            label=""
            className="m-1"
            checked={defaultOption === option.id}
            disabled={hiddenOptions.includes(option.id) || props.disableUserActions}
            onChange={() => props.onAccessAnalysisDefaultChange(option.id, accessAnalysisElement)}
            id={`access-reports-admin__add-product--default-${accessAnalysisElement}-${option.id}`}
            value={option.id}
            name={`product-option-${accessAnalysisElement}`}
            testId={`access-reports-admin__add-product__${accessAnalysisElement}--${option.name}`}
          />
        ),
        hide: (
          <QESCheckbox
            containerClassName="m-0"
            className="m-1"
            checked={hiddenOptions.includes(option.id)}
            disabled={defaultOption === option.id || props.disableUserActions}
            label=""
            testId={`access-reports-admin__add-product--hide-${accessAnalysisElement}-${option.id}`}
            onChange={() => props.onAccessAnalysisHideChange(option.id, accessAnalysisElement)}
          />
        ),
      };
      /* oxlint-enable */
    });

    const columnConfig = getColumnConfig(title);

    if (options.length > 0) {
      return (
        <Waffles<ProductDetailsTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          disableStickyHeader={true}
          fullWidth={true}
          testId={`access-reports-admin-product-details-${title}`}
        />
      );
    }
  }

  const getColumnConfig = (displayTitle: string): ColumnConfig => {
    return {
      default: {
        displayElement: <>Default<span className="text-danger">*</span></>,
        noSort: true,
        testId: "default",
      },
      hide: {
        displayText: "Hide",
        noSort: true,
        testId: "hide",
      },
      title: {
        displayText: displayTitle,
        noSort: true,
        testId: "title",
      },
    };
  }

  const getAddGroupButtonValidity = (): boolean => {
    if (!props.disableUserActions &&
      !!group &&
      !props.groups.includes(group)
    ) {
      return true;
    }
  }

  const getAddGroupValidationText = (): string => {
    if (props.groups.includes(group)) {
      return "A group with that name already exists";
    }
  }

  const onAddGroupClick = (): void => {
    props.onAddGroupClick(group);

    setGroup("");
  }

  const onGroupInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setGroup(event.target.value);
  }

  const addGroupValidationText: string = getAddGroupValidationText();

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <label className="label-m mb-2">
            Product
          </label>
        </div>
      </div>

      <div className="row align-items-end mb-3">
        <div className="col-md-3">
          <QESSelect
            allowEmpty={true}
            disabled={props.disableUserActions}
            isValid={!!productTypeValue}
            testId="access-reports-admin__add-product--product-type-dropdown"
            label="Product Type"
            required={true}
            onChange={(e) => props.onProductTypeChange(e)}
            value={productTypeValue}
          >
            {props.productTypes.map((productType, index) => (
              <option
                key={index}
                value={productType.id}
              >
                {productType.displayName ? productType.displayName : productType.name}
              </option>
            ))}
          </QESSelect>
        </div>

        <div className="col-md-3">
          <QESTextInput
            testId="access-reports-admin__add-product--product-input"
            disabled={props.disableUserActions}
            isValid={!!props.product.displayName || !!props.product.name}
            label="Product"
            onChange={(e) => props.onProductInputChange(e)}
            required={true}
            value={(props.product.displayName ? props.product.displayName : props.product.name) || ""}
          />
        </div>

        <div className="col-md-2">
          <QESCheckbox
            checked={props.product && props.product.editable}
            disabled={props.disableUserActions}
            label="Allow Edit"
            testId="access-reports-admin__add-product--allow-edit"
            onChange={() => props.onAllowEditToggle()}
          />
        </div>

        <div className="col-md-3">
          <QESCheckbox
            checked={props.product?.skipAccessAnalysis}
            disabled={props.disableUserActions}
            label="Skip Access Analysis"
            testId="access-reports-admin__add-product--skip-access-analysis"
            onChange={() => props.onSkipAccessAnalysisToggle()}
          />
        </div>
      </div>

      <div className="row align-items-top mb-3">
        <div className="col-md-3">
          <QESTextInput
            testId="access-reports-admin__add-product--report-title-one"
            errorText={props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleOne)}
            disabled={props.disableUserActions}
            isValid={!props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleOne)}
            label="Default Report Title"
            onChange={(e) => props.onReportTitleInputChange(AccessReportsCoverPageElementEnum.ReportTitleOne, e)}
            unrequiredValidation={true}
            value={props.reportTitleOne || ""}
          />
        </div>

        <div className="col-md-3">
          <QESTextInput
            testId="access-reports-admin__add-product--report-title-two"
            errorText={props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleTwo)}
            disabled={props.disableUserActions}
            isValid={!props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleTwo)}
            label={" ".replace(/ /g, "\u00a0")}
            onChange={(e) => props.onReportTitleInputChange(AccessReportsCoverPageElementEnum.ReportTitleTwo, e)}
            unrequiredValidation={true}
            value={props.reportTitleTwo || ""}
          />
        </div>

        <div className="col-md-3">
          <QESTextInput
            testId="access-reports-admin__add-product--report-title-three"
            errorText={props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleThree)}
            disabled={props.disableUserActions}
            isValid={!props.invalidElements.get(AccessReportsCoverPageElementEnum.ReportTitleThree)}
            label={" ".replace(/ /g, "\u00a0")}
            onChange={(e) => props.onReportTitleInputChange(AccessReportsCoverPageElementEnum.ReportTitleThree, e)}
            unrequiredValidation={true}
            value={props.reportTitleThree || ""}
          />
        </div>
      </div>

      {!props.product.skipAccessAnalysis && (
        <React.Fragment>
          <div className="row">
            <div className="col-12">
              <label className="label-m mb-2">
                Access Analysis
              </label>
            </div>
          </div>

          <div className="row flex-nowrap mb-3">
            <div className="col-4">
              {buildAccessAnalysisTable(AccessReportsAccessAnalysisElementEnum.CalculationMethod)}
            </div>
            <div className="col-4">
              {buildAccessAnalysisTable(AccessReportsAccessAnalysisElementEnum.DistanceMethod)}
            </div>
            <div className="col-4">
              {buildAccessAnalysisTable(AccessReportsAccessAnalysisElementEnum.AccessStandard)}
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <label className="label-m mb-2">
                Analysis Breakdowns
              </label>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <QESTextInput
                inputAppend={(
                  <QESButton
                    qesType="secondary"
                    className="p-0"
                    disabled={!getAddGroupButtonValidity()}
                    qesTestId="access-reports-admin__add-product--add-group"
                    onClick={() => onAddGroupClick()}
                  >
                    Add
                  </QESButton>
                )}
                testId="access-reports-admin__add-product--group-input"
                errorText={addGroupValidationText}
                disabled={props.disableUserActions}
                isValid={!addGroupValidationText}
                label="Groups"
                onChange={(e) => onGroupInputChange(e)}
                required={true}
                value={group}
              />

              {props.groups.length > 0 &&
                <React.Fragment>
                  <hr />

                  {props.groups.map((group, index) => (
                    <QESTextInput
                      inputAppend={(
                        <QESButton
                          className="p-0"
                          disabled={props.disableUserActions}
                          qesTestId={`access-reports-admin__add-product--remove-group-${index}`}
                          qesType="secondary-danger"
                          onClick={() => props.onRemoveGroupClick(group)}
                        >
                          Remove
                        </QESButton>
                      )}
                      key={index}
                      testId={`access-reports-admin__add-product--readonly-group-${index}`}
                      disabled={props.disableUserActions}
                      label={group}
                      hideLabel={true}
                      readOnly
                      value={group}
                    />
                  ))}
                </React.Fragment>
              }
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export { AccessReportsAdminAddProductDetails };
